/**
 * Step 4 — documents (optional).
 *
 * **CRITICAL a11y fix** (rcp-security B.5): the mockup uses a dashed-border
 * dropzone DIV with no focusable element — keyboard users can't reach it.
 * Here we wrap the dropzone in a `<label>` around a real `<input type="file">`,
 * so the dropzone gains native keyboard + click semantics with no extra code
 * (WCAG 2.1.1 keyboard; 4.1.2 name/role/value).
 *
 * File handling is local-only in the demo; real submission will ship via
 * PLANNED `/v3/bookings/{id}/uploads` (multipart, SP-backed).
 */

import { useCallback, useRef, type Dispatch, type ChangeEvent, type DragEvent } from 'react';
import type { BookingAction } from '../wizardState';
import type { BookingWizardState, UploadedFile, ServiceCode } from '../types';
import { Button } from '@/components/primitives';

export interface Step4DocumentsProps {
  state: BookingWizardState;
  dispatch: Dispatch<BookingAction>;
  onContinue: () => void;
  onBack: () => void;
}

interface RecommendedDoc {
  title: string;
  required: boolean;
}

const DOC_SUGGESTIONS: Record<ServiceCode, ReadonlyArray<RecommendedDoc>> = {
  DDA: [
    { title: 'Government-issued photo ID (front and back)', required: true },
    { title: 'Proof of funding source', required: false },
  ],
  SAV: [
    { title: 'Government-issued photo ID (front and back)', required: true },
    { title: 'Proof of funding source', required: false },
  ],
  CD: [
    { title: 'Government-issued photo ID (front and back)', required: true },
    { title: 'Proof of funding source (bank statement, if transferring from another bank)', required: false },
    { title: 'Beneficiary information (names, DOBs, SSNs)', required: false },
  ],
  IRA: [
    { title: 'Government-issued photo ID (front and back)', required: true },
    { title: 'Prior-year tax return or W-2 (for contribution limit review)', required: false },
    { title: 'Beneficiary information', required: false },
  ],
  SDB: [
    { title: 'Government-issued photo ID', required: true },
    { title: 'Both keys (close-out only)', required: false },
  ],
  UPDATE_INFO: [
    { title: 'Government-issued photo ID (front and back)', required: true },
  ],
  OTHER: [
    { title: 'Anything relevant to your question', required: false },
  ],
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function Step4Documents({ state, dispatch, onContinue, onBack }: Step4DocumentsProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const svcCode = state.service?.code ?? 'OTHER';
  const suggestions = DOC_SUGGESTIONS[svcCode];

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return;
      Array.from(files).forEach((f) => {
        const entry: UploadedFile = {
          id: `${f.name}-${f.size}-${Date.now()}`,
          name: f.name,
          sizeBytes: f.size,
          localOnly: true,
        };
        dispatch({ type: 'addFile', file: entry });
      });
    },
    [dispatch],
  );

  function onDrop(e: DragEvent<HTMLLabelElement>): void {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  }

  function onChange(e: ChangeEvent<HTMLInputElement>): void {
    handleFiles(e.target.files);
    // Reset so same file can be re-selected if user removes it.
    e.target.value = '';
  }

  return (
    <main className="rae-wizard__main">
      <button type="button" className="rae-wizard__back" onClick={onBack}>← Back</button>
      <h1 className="rae-wizard__h1">
        Documents <span className="rae-optional-badge">Optional</span>
      </h1>
      <p className="rae-wizard__lead">
        Uploading ahead of time makes your appointment faster. You can also bring originals to your appointment — totally up to you.
      </p>

      <section className="rae-card" aria-labelledby="rae-doc-h2">
        <h2 id="rae-doc-h2">Helpful for your appointment</h2>
        <p style={{ fontSize: 13, color: 'var(--ink-500)', marginBottom: 'var(--sp-7)' }}>
          For "{state.service?.title ?? 'your appointment'}" we suggest:
        </p>
        <ul className="rae-doc-list" aria-label="Recommended documents">
          {suggestions.map((doc) => (
            <li key={doc.title} className={`rae-doc ${doc.required ? 'is-required' : ''}`.trim()}>
              <span className="rae-doc__bullet" aria-hidden />
              <span className="rae-doc__name">{doc.title}</span>
              <span className="rae-doc__tag">
                {doc.required ? 'Required at appointment' : 'Helpful'}
              </span>
            </li>
          ))}
        </ul>

        <label
          className="rae-dropzone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.heic"
            aria-label="Choose files to upload"
            onChange={onChange}
          />
          <span className="rae-dropzone__icon" aria-hidden>📁</span>
          <span className="rae-dropzone__title">Drop files here</span>
          <span className="rae-dropzone__hint">
            or browse from your device · PDF, JPG, PNG, HEIC · up to 25 MB each
          </span>
          <span className="rae-dropzone__browse">Choose files</span>
        </label>

        {state.files.length > 0 && (
          <ul className="rae-uploaded" aria-label="Uploaded files">
            {state.files.map((f) => (
              <li key={f.id} className="rae-uploaded__row">
                <span aria-hidden>📄</span>
                <span className="rae-uploaded__name">{f.name}</span>
                <span className="rae-uploaded__size">{formatBytes(f.sizeBytes)}</span>
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={() => dispatch({ type: 'removeFile', id: f.id })}
                  style={{ color: 'var(--pcb-red)' }}
                  aria-label={`Remove ${f.name}`}
                >
                  ✕ Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        <div style={{ textAlign: 'center', marginTop: 'var(--sp-8)' }}>
          <button
            type="button"
            className="btn btn-link"
            style={{ color: 'var(--ink-500)', textDecoration: 'underline' }}
            onClick={onContinue}
          >
            Skip for now — I'll bring them to the appointment
          </button>
        </div>
      </section>

      <div className="rae-wizard__actions">
        <Button variant="ghost" size="lg" onClick={onBack}>← Back</Button>
        <Button variant="primary" size="lg" onClick={onContinue}>Continue →</Button>
      </div>
    </main>
  );
}
